/**
 * The closures on a map, and the rectangle being drawn for a new one.
 *
 * Drawing is a pointer gesture: press "Draw on the map", then drag from one
 * corner to the other. It is never the only way: the form's four coordinate
 * fields take the same rectangle from a keyboard (WCAG 2.1.1 and 2.5.7), and
 * whatever is drawn lands in those fields, where it can be read and
 * corrected. Escape leaves drawing mode. Every closure on the map is also a
 * row of the list, in words, so the map adds nothing a reader without it
 * misses.
 *
 * Leaflet is driven directly, as in `DayMap.tsx`. Colours come from tokens
 * through classes (`closures.css`); a closure is dashed and the draft is
 * dotted, so they differ by more than colour (WCAG 1.4.1).
 */
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./map.css";
import "./closures.css";
import { Button } from "@depicomnlbv/platform-ui";
import { useEffect, useId, useRef, useState } from "react";

import type { Closure, ClosureArea } from "../api";

const INITIAL_CENTRE: L.LatLngExpression = [53.25, -4.35];
const INITIAL_ZOOM = 9;
const FIT_MAX_ZOOM = 13;
const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const bounds = (a: ClosureArea) => L.latLngBounds([a.south, a.west], [a.north, a.east]);

/** A drawn rectangle rounded to four decimals, about 10 m, as the fields show it. */
function rounded(b: L.LatLngBounds): ClosureArea {
  const r = (n: number) => Math.round(n * 10_000) / 10_000;
  return { south: r(b.getSouth()), west: r(b.getWest()), north: r(b.getNorth()), east: r(b.getEast()) };
}

export function ClosuresMap({
  closures,
  draft,
  draftProblem,
  onDrawn,
}: {
  closures: Closure[];
  draft: ClosureArea | null;
  /** Why the draft cannot be recorded as it is (too large), or null. */
  draftProblem: string | null;
  onDrawn: (area: ClosureArea) => void;
}) {
  const captionId = useId();
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [ready, setReady] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const drawn = useRef(onDrawn);
  drawn.current = onDrawn;

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, {
      center: INITIAL_CENTRE,
      zoom: INITIAL_ZOOM,
      scrollWheelZoom: false,
    });
    L.tileLayer(TILES, { attribution: TILE_ATTRIBUTION, maxZoom: 18 }).addTo(instance);
    layer.current = L.layerGroup().addTo(instance);
    map.current = instance;
    setReady(true);
    return () => {
      instance.remove();
      map.current = null;
      layer.current = null;
    };
  }, []);

  // The closures and the draft, drawn again on every change.
  useEffect(() => {
    if (!ready || !layer.current || !map.current) return;
    const group = layer.current;
    group.clearLayers();
    for (const closure of closures) {
      L.rectangle(bounds(closure.area), {
        className: `map-closure map-closure--${closure.status}`,
        interactive: false,
      }).addTo(group);
    }
    if (draft) {
      L.rectangle(bounds(draft), { className: "map-closure map-closure--draft", interactive: false }).addTo(group);
    }
    const all = [...closures.map((c) => c.area), ...(draft ? [draft] : [])];
    if (all.length > 0) {
      const extent = all.map(bounds).reduce((acc, b) => acc.extend(b));
      map.current.fitBounds(extent, { padding: [24, 24], maxZoom: FIT_MAX_ZOOM, animate: false });
    }
  }, [ready, closures, draft]);

  // Drawing: the map stops panning, and a drag from corner to corner is the
  // rectangle. Pointer events rather than Leaflet's mouse events, so a finger
  // draws as a mouse does; `map--drawing` sets touch-action: none, or the
  // drag would scroll the page. A tap draws nothing and leaves drawing on.
  useEffect(() => {
    const instance = map.current;
    if (!ready || !instance || !drawing) return;
    const element = instance.getContainer();
    instance.dragging.disable();
    element.classList.add("map--drawing");
    let start: L.LatLng | null = null;
    let preview: L.Rectangle | null = null;
    const at = (event: PointerEvent) => instance.mouseEventToLatLng(event as unknown as MouseEvent);

    const down = (event: PointerEvent) => {
      // The zoom buttons and the attribution link stay usable.
      if ((event.target as HTMLElement).closest(".leaflet-control")) return;
      start = at(event);
      element.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    };
    const move = (event: PointerEvent) => {
      if (!start) return;
      const b = L.latLngBounds(start, at(event));
      if (preview) preview.setBounds(b);
      else preview = L.rectangle(b, { className: "map-closure map-closure--draft", interactive: false }).addTo(instance);
    };
    const up = (event: PointerEvent) => {
      if (!start) return;
      const b = L.latLngBounds(start, at(event));
      start = null;
      preview?.remove();
      preview = null;
      if (b.getNorth() > b.getSouth() && b.getEast() > b.getWest()) {
        drawn.current(rounded(b));
        setDrawing(false);
      }
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawing(false);
    };
    element.addEventListener("pointerdown", down);
    element.addEventListener("pointermove", move);
    element.addEventListener("pointerup", up);
    element.addEventListener("pointercancel", up);
    document.addEventListener("keydown", key);
    return () => {
      element.removeEventListener("pointerdown", down);
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerup", up);
      element.removeEventListener("pointercancel", up);
      document.removeEventListener("keydown", key);
      preview?.remove();
      instance.dragging.enable();
      element.classList.remove("map--drawing");
    };
  }, [ready, drawing]);

  return (
    <section aria-labelledby="closures-map-heading" className="panel closures-map">
      <h3 id="closures-map-heading">Map</h3>
      <p className="panel__caption" id={captionId}>
        {drawing
          ? "Drag from one corner of the closed area to the other. Escape stops drawing."
          : "Every closure on the map is also in the list. The new closure's area can be drawn here or typed in the form."}
      </p>
      <div className="closures-map__tools">
        {/* The label says the state; no aria-pressed as well, or a screen
            reader hears "Stop drawing, pressed". */}
        <Button onPress={() => setDrawing((on) => !on)} variant="secondary">
          {drawing ? "Stop drawing" : "Draw on the map"}
        </Button>
      </div>
      {draftProblem ? (
        <p className="closure-form__error" role="status">
          {draftProblem}
        </p>
      ) : null}
      <div
        aria-describedby={captionId}
        aria-label="Map of road closures. Arrow keys pan, plus and minus zoom."
        className="map"
        ref={container}
        role="application"
      />
      <ul aria-label="Map legend" className="map-legend">
        <li>
          <span aria-hidden="true" className="map-legend__closure" />
          Road closure, dashed outline
        </li>
        <li>
          <span aria-hidden="true" className="map-legend__closure map-legend__closure--draft" />
          The new closure, dotted outline
        </li>
      </ul>
    </section>
  );
}
